import { useEffect, useRef, useState } from 'react'
import { useTonConnectUI } from '@tonconnect/ui-react'
import { useStyles } from './styles'

import { BalanceText } from '@/shared/ui/balanceText'
import { ProgressBar } from '@/shared/ui/progressBar'
import { AppButton } from '@/shared/ui/button'
import { Header } from '@/shared/ui/header'
import { UnsubscribedUserDrawer } from '@/shared/ui/subscriptionDrawers/unsubscribedUserDrawer'

import DailyCaseBanner from '@/features/dailyCaseBanner'
import { SwiperPlatformList } from '@/features/platformsList/swiperList'

import { useUnit } from 'effector-react'
import {
  $gigiBalance,
  $tgAccuntData,
  $updateProgressCooldownValues,
  $userBalance,
  $userLanguage,
  $userToken,
  authUserFx,
  PremiumLevel,
} from '@/entities/user'
import { getSteamAuthUrlFx } from '@/entities/user'
import { $isUserGamedPremium, $userSettings, $premiumLevel } from '@/entities/user'
import { getUserProfileFx } from '@/entities/user'
import { getSteamRefreshFx } from '@/entities/platforms/steam/effects'
import { goToConnectPlatform } from '@/app/routing/events'
import { connectFarcanaEvent, connectSteamEvent } from '@/entities/platforms/events'
import { lang } from '@/shared/constants/lang'

import { insertUserSettings } from '@/entities/user/api'
import { getCaseListFx } from '@/entities/cases/effects'
import AppDrawer from '@/features/drawer'

import useScrollTracking from '@/shared/hooks/useScrollTracking'
import { SubscribedUserDrawer } from '@/shared/ui/subscriptionDrawers/subscribedUserDrawer'

import { Icon } from '@/shared/ui/icon'
import { useTimer } from '@/shared/hooks/useTimer'
import { TimeDigits } from '@/shared/constants/types'
import WebApp from '@twa-dev/sdk'
import { DailyBonusPanel } from '@/features/dailyBonusPanel'
import { getDailyBonusInfoFx } from '@/entities/dailyBonus/model/effects'
import { NewsList } from '@/features/newsList'

import { $dailyBonusInfo } from '@/entities/dailyBonus/model/store'
import { $casesList } from '@/entities/cases/store'
import { GigiBalance } from '@/features/gigiBalance'
import { darkTheme } from '@/shared/styles/theme/dark'
import { useGetLastTimeForCooldown } from '@/shared/hooks/useGetLastTimeForCooldown'
import { usePostStory } from '@/shared/hooks/usePostStory'
import { useScrollRestoration } from '@/shared/hooks/useRestoreScroll'
import { $steamUserData } from '@/entities/platforms/steam/store'
import { $steam } from '@/entities/platforms/store'
import { AnalyticsEvents } from '@/entities/analytics'
import { FriendsPressedButton } from '@/features/friendsPressedButton'
import { getScoringInvoice } from '@/entities/payments/api'
import { StarterPackMainFrame } from '@/pages/starterPackPage/ui/starterPackMainFrame'
import { $specialOfferInfo, getSpecialOfferFx } from '@/entities/payments'

const Main = () => {
  const { classes } = useStyles({})
  const steamData = useUnit($steamUserData)

  const userBalance = useUnit($userBalance)
  const userPhoto = steamData?.userData?.photoUrl ? steamData.userData.photoUrl : ''
  const userSettings = useUnit($userSettings)
  const steamId = steamData?.userData?.steamId ?? null
  const userToken = useUnit($userToken)
  const userLang = useUnit($userLanguage)
  const isUserGamedPremium = useUnit($isUserGamedPremium)
  const userSubscriptionLevel = useUnit($premiumLevel)
  const telegramData = useUnit($tgAccuntData)
  const steamStatus = useUnit($steam).status
  const texts = lang[userLang].pages.main
  const dailyBonusInfo = useUnit($dailyBonusInfo)
  const casesList = useUnit($casesList)
  const gigi = useUnit($gigiBalance)
  const updateCooldownValues = useUnit($updateProgressCooldownValues)
  const { startDate, endDate } = useGetLastTimeForCooldown({
    ...updateCooldownValues,
    kind: 'scoring',
  })

  const { progressBarWidth, timeLeft, canUpdate, skipPrice } = useTimer(endDate.toISOString(), startDate.toISOString())
  const noWalletSettings = userSettings.tonWalletConnected == null || userSettings.tonWalletConnected == undefined
  const tonWalletConnected = useTonConnectUI()[0].connected
  const specialOffer = useUnit($specialOfferInfo)
  const { canShareStory, isMakingStoryLink, handleShareStory } = usePostStory()

  useEffect(() => {
    if (userToken && !specialOffer) {
      getSpecialOfferFx(userToken)
    }
  }, [specialOffer, userToken])

  useEffect(() => {
    if (!steamData) {
      if (userToken) {
        getUserProfileFx(userToken)
      }
    }
  }, [userToken, steamData])

  useEffect(() => {
    if (tonWalletConnected && userToken && noWalletSettings) {
      setTimeout(
        () =>
          insertUserSettings({
            token: userToken,
            data: {
              key: AnalyticsEvents.TON_WALLET_CONNECTED,
              value: true,
            },
          }),
        3000
      )
    }
  }, [tonWalletConnected, userToken, noWalletSettings])

  useEffect(() => {
    if (userToken !== null && steamId == null) {
      getSteamAuthUrlFx(userToken)
    }
    if (steamId !== null && userToken !== null) {
      connectSteamEvent()
    }
    if (!casesList && userToken) {
      getCaseListFx({ token: userToken })
    }
    if (userSettings.farcana_jwt) {
      connectFarcanaEvent()
    }
    if (userToken === null) {
      authUserFx()
    } else {
      if (!dailyBonusInfo) {
        getDailyBonusInfoFx(userToken)
      }
    }
  }, [userToken, steamId, userSettings, dailyBonusInfo, casesList])

  const [invoiceLoading, setInvoiceLoading] = useState(false)
  async function handleBuyUpdate() {
    setInvoiceLoading(true)

    const link = await getScoringInvoice(userToken!)
    WebApp.openInvoice(link.invoice_link, (status) => {
      setInvoiceLoading(false)
      if (status === 'paid') {
        getUserProfileFx(userToken!)
      }
    })
  }

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  async function handleTriggerUpdate() {
    if (steamStatus === 'Available') {
      goToConnectPlatform({ platform: 'steam' })
      return
    }

    if (userToken) {
      setIsUpdateLoading(true)
      try {
        await getSteamRefreshFx(userToken)
        localStorage.setItem('lastUpdateDate', new Date().toISOString())
      } finally {
        await getUserProfileFx(userToken)
        setTimeout(() => setIsUpdateLoading(false), 1000)
      }
    }
  }

  const scrollRef = useRef<HTMLDivElement>(null)
  const hasScrolled = useScrollTracking(scrollRef, 20)
  useScrollRestoration(scrollRef)
  return (
    <div className={classes.contentWrapper} ref={scrollRef}>
      <AppDrawer hidden={hasScrolled}>
        {userSubscriptionLevel > PremiumLevel.UNSUBSCRIBED ? (
          <SubscribedUserDrawer subscriptionLevel={userSubscriptionLevel} />
        ) : (
          <UnsubscribedUserDrawer />
        )}
      </AppDrawer>
      <Header
        classes={{ root: classes.header }}
        userPhotoUrl={userPhoto}
        userName={telegramData?.user.first_name}
        isPlusUser={isUserGamedPremium}
      />

      <div className={classes.balanceUpdateWrapper}>
        <div className={classes.balanceTextWrapper}>
          <span>{texts.gamedRewards}</span>
          <BalanceText balance={userBalance} />
        </div>
        <GigiBalance balance={gigi} modalStyles={{ width: '90%', left: '5%', top: '90px' }} />
      </div>
      {specialOffer && <StarterPackMainFrame {...specialOffer} />}
      <div className={classes.shareButtons}>
        <AppButton
          isLoading={invoiceLoading || isUpdateLoading}
          variant="secondary"
          className={classes.shareLastButton}
          onClick={canUpdate ? handleTriggerUpdate : handleBuyUpdate}
        >
          <UpdateText canUpdate={canUpdate} timeLeft={timeLeft} skipPrice={skipPrice} />
          <ProgressBar width={progressBarWidth} color={darkTheme.colors.button.primary.background} />
        </AppButton>

        <FriendsPressedButton />
      </div>

      <DailyBonusPanel />
      <AppButton
        isLoading={isMakingStoryLink}
        onClick={handleShareStory}
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '90%' }}
        disabled={!canShareStory}
      >
        {texts.shareLegacy}
      </AppButton>

      <h3 className={classes.title}>{texts.rewards}</h3>
      <DailyCaseBanner />

      <h3 className={classes.title}>{texts.news}</h3>
      <NewsList />
      <h3 className={classes.title}>{texts.platforms}</h3>
      <SwiperPlatformList />
    </div>
  )
}

export default Main

function UpdateText({
  canUpdate,
  timeLeft,
  skipPrice,
}: {
  canUpdate: boolean
  timeLeft: TimeDigits
  skipPrice: number
}) {
  const { classes } = useStyles()
  const userLang = useUnit($userLanguage)

  if (!canUpdate)
    return (
      <span className={classes.updateText}>
        <Icon view="rocket" invert />
        <span>
          <p>{lang[userLang].pages.main.updateNow}</p>
          {skipPrice}

          <Icon view="tgStar" style={{ height: '16px', width: '16px' }} />
        </span>
        {`${timeLeft.hours}:${timeLeft.minutes}:${timeLeft.seconds}`}
      </span>
    )
  return (
    <span className={classes.updateText}>
      <Icon view="lightning" style={{ paddingBottom: '2px' }} />
      {lang[userLang].pages.main.update_progress}
    </span>
  )
}
